import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import { FileText, ShieldCheck, XCircle, Award, Check, RefreshCw } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import heenaSigImg from '../../assets/heena_prasad_signature.png';
import doxezLogo from '../../assets/DoxezLOGO.png';
import hedenaLogo from '../../assets/HEDENALOGO.jpeg';

const getSignatureUrl = (sig) => {
    if (!sig) return null;

    if (sig.startsWith('http://') || sig.startsWith('https://')) {
        return sig;
    }

    const normalizedPath = sig.replace(/\\/g, '/');
    const cleanPath = normalizedPath.startsWith('/') ? normalizedPath.substring(1) : normalizedPath;
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    if (isLocal) {
        return `http://localhost:8000/${cleanPath}`;
    }

    return `https://crm.doxez.in/${cleanPath}`;
};

import { generateAgreementPDF, replacePlaceholders } from '../../utils/pdfGenerator';

const MoUConsent = () => {
    const { token } = useParams();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [mouData, setMouData] = useState(null);
    const [decision, setDecision] = useState(null); // 'agree' | 'disagree'

    useEffect(() => {
        const fetchDetails = async () => {
            try {
                const response = await api.get(`/auth/mou-details/${token}`);
                setMouData(response.data.data);
            } catch (err) {
                console.error(err);
                toast.error(err.response?.data?.message || 'Failed to fetch MoU details. Link may be expired.');
            } finally {
                setLoading(false);
            }
        };
        fetchDetails();
    }, [token]);

    const handleAgree = async () => {
        setSubmitting(true);
        const uploadLoadId = toast.loading('Compiling and signing your agreement PDF...');
        try {
            const fetchImageAsBase64 = async (url) => {
                try {
                    const directResp = await fetch(url);
                    if (directResp.ok) {
                        const blob = await directResp.blob();
                        return await new Promise((resolve) => {
                            const reader = new FileReader();
                            reader.onloadend = () => resolve(reader.result);
                            reader.onerror = () => resolve(null);
                            reader.readAsDataURL(blob);
                        });
                    }
                } catch (e) {
                    try {
                        const proxyResp = await fetch(`/api/upload/proxy?url=${encodeURIComponent(url)}`);
                        if (proxyResp.ok) {
                            const blob = await proxyResp.blob();
                            return await new Promise((resolve) => {
                                const reader = new FileReader();
                                reader.onloadend = () => resolve(reader.result);
                                reader.onerror = () => resolve(null);
                                reader.readAsDataURL(blob);
                            });
                        }
                    } catch (err) {
                        console.error('Failed to proxy sig', err);
                    }
                }
                return null;
            };

            let signatureImg = null;

            if (mouData.user.role === 'hospital' && mouData.user.onboardingData?.authorizedSignatories) {
                signatureImg = [];
                const signatories = mouData.user.onboardingData.authorizedSignatories;
                for (let i = 0; i < signatories.length; i++) {
                    const sigDoc = mouData.user.onboardingDocuments?.find(d => d.type === `SIGNATORY_SIGNATURE_${i}`);
                    const rawUrl = signatories[i].signatureUrl || (sigDoc ? sigDoc.url : null);
                    let dataUrl = null;
                    if (rawUrl) {
                        const sigUrl = getSignatureUrl(rawUrl);
                        if (sigUrl) {
                            dataUrl = await fetchImageAsBase64(sigUrl);
                        }
                    }
                    signatureImg.push({
                        name: signatories[i].name || 'Authorized Signatory',
                        designation: signatories[i].designation || signatories[i].role || 'Authorized Representative',
                        img: dataUrl
                    });
                }
            } else {
                const rawSignature = mouData.user.signature;
                const signatureUrl = getSignatureUrl(rawSignature);
                if (signatureUrl) {
                    signatureImg = await fetchImageAsBase64(signatureUrl);
                }
            }

            const doc = generateAgreementPDF(
                mouData.user.role,
                mouData.user,
                signatureImg,
                heenaSigImg,
                mouData.template
            );

            const pdfBlob = doc.output('blob');
            const signedAgreementName = `Signed_Agreement_${mouData.user.role.toUpperCase()}_${Date.now()}.pdf`;
            const file = new File([pdfBlob], signedAgreementName, { type: 'application/pdf' });

            const upData = new FormData();
            upData.append('documents', file);

            // Upload using the public onboarding file upload endpoint
            const uploadResp = await api.post(`/upload/onboarding/${token}`, upData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            let uploadedUrl = null;
            if (uploadResp.data.data && uploadResp.data.data.files) {
                uploadedUrl = uploadResp.data.data.files[0].url;
            } else if (uploadResp.data.data && uploadResp.data.data.url) {
                uploadedUrl = uploadResp.data.data.url;
            }

            if (!uploadedUrl) {
                throw new Error("Agreement upload failed. Unable to acquire S3 link.");
            }

            // Submit MoU consent to activate account
            await api.post('/auth/mou-consent', {
                token,
                status: 'agree',
                agreementUrl: uploadedUrl
            });

            toast.success('MoU signed and account activated successfully!', { id: uploadLoadId });
            setDecision('agree');
            // Trigger browser download
            doc.save(signedAgreementName);
        } catch (err) {
            console.error('MOU_CONSENT_AGREE_ERROR:', err);
            toast.error(err.response?.data?.message || err.message || 'Verification failed', { id: uploadLoadId });
        } finally {
            setSubmitting(false);
        }
    };

    const handleDisagree = async () => {
        if (!window.confirm("Are you sure you want to decline the MoU? This will cancel your registration request.")) {
            return;
        }

        setSubmitting(true);
        const loadId = toast.loading('Declining MoU agreement...');
        try {
            await api.post('/auth/mou-consent', {
                token,
                status: 'disagree'
            });
            toast.success('Registration cancelled successfully', { id: loadId });
            setDecision('disagree');
        } catch (err) {
            console.error('MOU_CONSENT_DISAGREE_ERROR:', err);
            toast.error(err.response?.data?.message || 'Request failed', { id: loadId });
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-900 flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <RefreshCw className="w-12 h-12 text-blue-500 animate-spin" />
                    <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">Loading MoU details...</p>
                </div>
            </div>
        );
    }

    if (!mouData) {
        return (
            <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-slate-800 border border-slate-700/80 p-8 rounded-3xl text-center shadow-2xl space-y-6">
                    <XCircle className="w-16 h-16 text-rose-500 mx-auto" />
                    <h2 className="text-xl font-black text-white uppercase tracking-tight">Invalid or Expired Link</h2>
                    <p className="text-slate-400 text-sm leading-relaxed">This MoU review link is invalid, expired, or has already been completed. Please contact support or your account administrator to obtain a new link.</p>
                </div>
            </div>
        );
    }

    if (decision === 'agree') {
        return (
            <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-slate-800 border border-slate-700/80 p-8 rounded-[32px] text-center shadow-2xl space-y-6">
                    <div className="w-16 h-16 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl flex items-center justify-center text-emerald-500 mx-auto shadow-inner">
                        <ShieldCheck className="w-10 h-10" />
                    </div>
                    <h2 className="text-2xl font-black text-emerald-400 uppercase tracking-tight">MoU Signed Successfully</h2>
                    <p className="text-slate-300 text-sm leading-relaxed">Thank you, <strong>{mouData.user.fullName}</strong>. You have agreed to the Memorandum of Understanding. Your account has been activated, and a copy of the signed MoU is saved to your profile.</p>
                    <div className="bg-slate-900/50 p-4 rounded-2xl border border-slate-700/35 text-left text-xs text-slate-400 space-y-2">
                        <p>✓ Account activated on Doxez Network</p>
                        <p>✓ Credentials & Password dispatched to <strong>{mouData.user.email}</strong></p>
                        <p>✓ Branded MoU copy downloaded to your device</p>
                    </div>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider">You may close this tab now.</p>
                </div>
            </div>
        );
    }

    if (decision === 'disagree') {
        return (
            <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-slate-800 border border-slate-700/80 p-8 rounded-[32px] text-center shadow-2xl space-y-6">
                    <XCircle className="w-16 h-16 text-rose-500 mx-auto" />
                    <h2 className="text-xl font-black text-rose-400 uppercase tracking-tight">Agreement Declined</h2>
                    <p className="text-slate-300 text-sm leading-relaxed">You have declined the Memorandum of Understanding. As a result, your registration request has been cancelled and deactivated from the system.</p>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider">Please close this window.</p>
                </div>
            </div>
        );
    }

    let resolvedContent = replacePlaceholders(mouData.template?.content, mouData.user, mouData.user.role);
    const resolvedTitle = replacePlaceholders(mouData.template?.title, mouData.user, mouData.user.role);

    if (resolvedContent) {
        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(resolvedContent, 'text/html');
            const paragraphs = doc.querySelectorAll('p');

            paragraphs.forEach(p => {
                const plainText = p.textContent.trim();
                if (plainText.length < 2 || plainText.length > 200) return;

                const startsWithNumber = /^\d+(\.\d+)*\.?\s+/.test(plainText);
                const isAllUppercase = /^[A-Z0-9\s\.\&\-\(\)]+$/.test(plainText);
                const isShort = plainText.length < 100;

                let isHeading = false;
                if (isAllUppercase && isShort) {
                    if (plainText.length >= 15 || startsWithNumber) {
                        isHeading = true;
                    }
                } else if (startsWithNumber && isShort) {
                    const isDate = /january|february|march|april|may|june|july|august|september|october|november|december/i.test(plainText);
                    if (!isDate) {
                        isHeading = true;
                    }
                }

                if (isHeading) {
                    const h4 = doc.createElement('h4');
                    while (p.firstChild) {
                        h4.appendChild(p.firstChild);
                    }
                    Array.from(p.attributes).forEach(attr => h4.setAttribute(attr.name, attr.value));
                    p.parentNode.replaceChild(h4, p);
                }
            });

            resolvedContent = doc.body.innerHTML;
        } catch (e) {
            console.error('DOMParser heading extraction failed:', e);
        }
    }

    return (
        <div className="min-h-screen bg-slate-900 text-slate-100 py-12 px-4 flex flex-col items-center">
            {/* Header branding */}
            <div className="max-w-4xl w-full flex flex-col sm:flex-row items-center justify-between gap-6 mb-8">
                <div className="flex items-center gap-4">
                    <div className="h-16 w-32 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center p-2 backdrop-blur-md">
                        <img src={doxezLogo} alt="DOXEZ Logo" className="h-10 object-contain" />
                    </div>
                    <div>
                        <h1 className="text-xl font-black tracking-tight text-white uppercase italic">MoU <span className="text-blue-500">CONSENT</span></h1>
                        <p className="text-[10px] text-slate-400 uppercase tracking-widest mt-0.5 font-bold">Hedena Healthcare Private Limited</p>
                    </div>
                </div>
                <div className="bg-amber-500/10 border border-amber-500/20 text-amber-500 font-black uppercase text-[9px] tracking-widest px-4 py-2.5 rounded-full flex items-center gap-2">
                    <Award className="w-3.5 h-3.5" /> Action Required: Review & Sign
                </div>
            </div>

            {/* Main Agreement Page */}
            <div className="max-w-4xl w-full bg-white text-slate-800 p-8 sm:p-14 rounded-3xl shadow-2xl border border-slate-200/10 flex flex-col relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 to-indigo-600"></div>

                {/* MoU Title */}
                <div className="text-center pb-6 border-b border-slate-100 mb-8">
                    <h2 className="text-[#143E78] text-lg sm:text-xl font-black uppercase tracking-tight leading-snug">
                        {resolvedTitle || "MEMORANDUM OF UNDERSTANDING (MOU)"}
                    </h2>
                </div>

                {/* MoU Dynamic Scrollable Content */}
                {/* MoU Dynamic Scrollable Content */}
                <div
                    className="text-xs text-slate-700 space-y-3 text-justify [&_p]:text-justify [&_li]:text-justify [&_h4]:font-black [&_h4]:text-sm [&_h4]:text-[#143E78] [&_h4]:mt-4 [&_h4]:mb-1 [&_h5]:font-bold [&_h5]:text-xs [&_h5]:text-slate-800 [&_h5]:mt-3 [&_h5]:mb-1 [&_strong]:font-bold [&_b]:font-black [&_p]:leading-relaxed [&_p]:mb-2 [&_ul]:list-disc [&_ul]:ml-5 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:ml-5 [&_ol]:mb-3 [&_li]:mb-1"
                    dangerouslySetInnerHTML={{ __html: resolvedContent }}
                />

                {/* MoU Signatures Display */}
                <div className="mt-12 pt-8 border-t border-slate-100">
                    <h4 className="text-xs font-black text-slate-800 uppercase tracking-widest mb-8">SIGNATORIES</h4>
                    <div className="grid grid-cols-2 gap-8 text-xs">
                        <div className="space-y-4">
                            <p className="font-bold text-slate-700">For Hedena Healthcare Private Limited</p>
                            <div className="h-20 flex items-end">
                                <img src={heenaSigImg} alt="Hedena Director Signature" className="h-14 object-contain" />
                            </div>
                            <div className="border-t border-slate-200 pt-2 space-y-1">
                                <p className="font-bold text-slate-800">Name: Dr. Heena Prasad</p>
                                <p className="text-slate-400 text-[10px] uppercase font-bold tracking-wider">Designation: Managing Director</p>
                                <p className="text-slate-400 text-[10px]">Date: {new Date().toLocaleDateString()}</p>
                            </div>
                        </div>

                        <div className="flex flex-col gap-6">
                            {mouData.user.role === 'hospital' ? (
                                (mouData.user.onboardingData?.authorizedSignatories || [{}]).map((sig, idx) => {
                                    const sigDoc = mouData.user.onboardingDocuments?.find(d => d.type === `SIGNATORY_SIGNATURE_${idx}`);
                                    const sigUrl = sig.signatureUrl ? getSignatureUrl(sig.signatureUrl) : (sigDoc ? getSignatureUrl(sigDoc.url) : null);
                                    return (
                                        <div key={idx} className="space-y-4 border-b border-slate-100 pb-4 last:border-0 last:pb-0">
                                            <p className="font-bold text-slate-700">For Hospital / Healthcare Facility</p>
                                            <div className="h-20 flex items-end">
                                                {sigUrl ? (
                                                    <img src={sigUrl} alt={`Signatory ${idx + 1}`} className="h-14 object-contain" />
                                                ) : (
                                                    <span className="text-[10px] text-slate-400 font-mono italic">[Signature Pending]</span>
                                                )}
                                            </div>
                                            <div className="border-t border-slate-200 pt-2 space-y-1">
                                                <p className="font-bold text-slate-800">Name: {sig.name || mouData.user.firstName || 'Authorized Signatory'}</p>
                                                <p className="text-slate-400 text-[10px] uppercase font-bold tracking-wider">Designation: {sig.designation || sig.role || 'Authorized Representative'}</p>
                                                <p className="text-slate-400 text-[10px]">Date: {new Date().toLocaleDateString()}</p>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="space-y-4">
                                    <p className="font-bold text-slate-700">
                                        {mouData.user.role === 'doctor' && 'Empanelled Medical Professional'}
                                        {mouData.user.role === 'marketing_agent' && 'Outreach Partner'}
                                    </p>
                                    <div className="h-20 flex items-end">
                                        {mouData.user.signature ? (
                                            <img src={getSignatureUrl(mouData.user.signature)} alt="User Signature" className="h-14 object-contain" />
                                        ) : (
                                            <span className="text-[10px] text-slate-400 font-mono italic">[Signature Provided at Registration]</span>
                                        )}
                                    </div>
                                    <div className="border-t border-slate-200 pt-2 space-y-1">
                                        <p className="font-bold text-slate-800">Name: {mouData.user.fullName}</p>
                                        <p className="text-slate-400 text-[10px] uppercase font-bold tracking-wider">
                                            {mouData.user.role === 'doctor' && 'Designation: Medical Specialist'}
                                            {mouData.user.role === 'marketing_agent' && 'Designation: Partner'}
                                        </p>
                                        <p className="text-slate-400 text-[10px]">Date: {new Date().toLocaleDateString()}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Consent Options */}
            <div className="max-w-4xl w-full bg-slate-800/80 border border-slate-700/50 p-6 rounded-3xl shadow-xl mt-8 flex flex-col sm:flex-row items-center justify-between gap-6 backdrop-blur-md">
                <div className="flex gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400 shrink-0">
                        <FileText className="w-5 h-5" />
                    </div>
                    <div>
                        <p className="text-xs font-black uppercase text-white tracking-widest">Consent Required</p>
                        <p className="text-[10px] text-slate-400 mt-0.5 leading-normal">By clicking "Agree & Sign", you execute this MoU and activate your credentials.</p>
                    </div>
                </div>
                <div className="flex gap-4 w-full sm:w-auto">
                    <button
                        onClick={handleDisagree}
                        disabled={submitting}
                        className="flex-1 sm:flex-none px-6 py-3.5 border-2 border-slate-600 hover:border-slate-500 text-slate-300 rounded-2xl text-xs font-black uppercase tracking-widest transition-all disabled:opacity-50"
                    >
                        Decline MoU
                    </button>
                    <button
                        onClick={handleAgree}
                        disabled={submitting}
                        className="flex-1 sm:flex-none px-8 py-3.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-2xl text-xs font-black uppercase tracking-widest transition-all shadow-lg shadow-blue-900/30 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                        {submitting ? 'Processing...' : (
                            <>
                                <Check className="w-4 h-4" /> Agree & Sign
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MoUConsent;
